import { CreateSigningAppointmentFormData } from './convertToCreateSigningAppointmentRequest';
import React, { useMemo } from 'react';
import { eSigningMethod } from '../../../../../types/signingAppointment/eSigningMethod';
import { ThemeProvider } from 'styled-components';
import { Alert, Flex, ThemeConstellation } from '@zillow/constellation';
import { isDigitalSchedulingOwnerType } from '../../../../../types/order/consumer/eCreateSigningAppointmentFormDataOwnerTypes';
import { useHasFeatureFlag } from 'selectors/useHasFeatureFlag';

export const DRAFT_MESSAGE_ATTORNEY = `By clicking "Save Draft" no emails will be sent externally to the signers or agents, but your progress will be saved for later.`;
export const DRAFT_MESSAGE_SIG_SYNC =
  'This is a draft appointment. No emails will be sent to the signer. You will have an option to send after the appointment is created.';
export const SEND_EMAIL_MESSAGE_ATTORNEY = `Click "Send Email" to send the scheduling link to all selected signers and agents so they can make their selections. Selections will be returned within this scheduling tab, as well as orders@spruce.co, which will be blind copied on the emails.`;
export const SEND_EMAIL_MESSAGE =
  'Once "Send Email" is selected, all selected signers, and their respective agents will be emailed a scheduling link that will give access to make signing selections. Any selections made by any person accessing the enclosed link will be sent to Spruce and any integration that have been enable for this signing appointment. In addition "orders@spruce.co" will be blind copied.';

const useGetMessage = (wizardFormData: CreateSigningAppointmentFormData): string[] => {
  const isZillowClosing = useHasFeatureFlag('pinecone.zillow-closing');
  const ordersEmail = isZillowClosing ? 'orders@zillowclosing.com' : 'orders@spruce.co';

  return useMemo(() => {
    const sendEmailMessageAttorney = isZillowClosing
      ? SEND_EMAIL_MESSAGE_ATTORNEY.replace('orders@spruce.co', ordersEmail)
      : SEND_EMAIL_MESSAGE_ATTORNEY;
    const sendEmailMessage = isZillowClosing
      ? SEND_EMAIL_MESSAGE.replace('orders@spruce.co', ordersEmail).replace('sent to Spruce', 'sent to Zillow Closing')
      : SEND_EMAIL_MESSAGE;

    // Guard against non-DSS appointments
    if (!isDigitalSchedulingOwnerType(wizardFormData.ownerType)) {
      return [];
    }
    if (wizardFormData.signingMethod === eSigningMethod.attorney) {
      return [DRAFT_MESSAGE_ATTORNEY, sendEmailMessageAttorney];
    }
    if (wizardFormData.signingMethod !== eSigningMethod.notaryOnly) {
      return [];
    }
    if (!wizardFormData.isSignatureSyncAppointment) {
      return [DRAFT_MESSAGE_SIG_SYNC];
    }
    return [sendEmailMessage];
  }, [wizardFormData, ordersEmail, isZillowClosing]);
};

export const DssAlert: React.FC<{ wizardFormData: CreateSigningAppointmentFormData }> = ({ wizardFormData }) => {
  const messages = useGetMessage(wizardFormData);
  if (messages.length === 0) {
    return null;
  }
  return (
    <div className={'py-2'} data-test-id={'dss-alert'}>
      <ThemeProvider theme={ThemeConstellation as any}>
        <Flex display={'flex'} flexDirection={'column'} gap={'sm'}>
          {messages.map((message) => (
            <Alert key={message} body={message} />
          ))}
        </Flex>
      </ThemeProvider>
    </div>
  );
};
